import { useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { X, Send, Loader2, CheckCircle2, UserRound, Phone, Mail } from "lucide-react";
import { KioskButton, useKiosk } from "./ui";
import { submitLead } from "@/lib/kioskData";
import { formatApiError } from "@/lib/api";

export function ConnectButton({ className }) {
  const { setSuppressIdle } = useKiosk() || {};
  const [open, setOpen] = useState(false);
  const show = () => { setSuppressIdle && setSuppressIdle(true); setOpen(true); };
  const hide = () => { setSuppressIdle && setSuppressIdle(false); setOpen(false); };

  return (
    <>
      <KioskButton testId="kiosk-connect-with-us" className={className} onClick={show}>
        <Send className="h-5 w-5" /> Connect With Us
      </KioskButton>
      {createPortal(
        <AnimatePresence>{open && <ConnectModal onClose={hide} />}</AnimatePresence>,
        document.body
      )}
    </>
  );
}

function Field({ icon: Icon, testId, ...props }) {
  return (
    <label className="block">
      <span className="relative flex items-center">
        <Icon className="pointer-events-none absolute left-4 h-5 w-5 text-cyan-300/70" />
        <input
          data-testid={testId}
          className="w-full rounded-2xl border border-cyan-400/20 bg-[#050b18] py-4 pl-12 pr-4 text-base text-white placeholder-slate-500 outline-none transition-colors focus:border-cyan-400/60"
          {...props}
        />
      </span>
    </label>
  );
}

function ConnectModal({ onClose }) {
  const [name, setName] = useState("");
  const [mobile, setMobile] = useState("");
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!name.trim() || !mobile.trim() || !email.trim()) {
      setErr("Please fill in all fields.");
      return;
    }
    setLoading(true);
    try {
      await submitLead({ name: name.trim(), mobile: mobile.trim(), email: email.trim() });
      setDone(true);
    } catch (e2) {
      setErr(formatApiError(e2?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      data-testid="kiosk-connect-overlay"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      transition={{ duration: 0.2 }}
      className="fixed inset-0 z-[70] flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.92, opacity: 0, y: 12 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0, y: 8 }}
        transition={{ type: "spring", stiffness: 240, damping: 24 }}
        className="relative w-full max-w-md rounded-3xl border border-cyan-400/20 bg-[#0b1730] p-6 sm:p-8 shadow-[0_0_50px_rgba(0,229,255,0.18)]"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="kiosk-connect-close"
          onClick={onClose}
          className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-xl glass glow-border-hover text-cyan-200"
          aria-label="Close"
        >
          <X className="h-5 w-5" />
        </button>

        {done ? (
          <div className="flex flex-col items-center py-6 text-center" data-testid="kiosk-connect-success">
            <div className="flex h-16 w-16 items-center justify-center rounded-full border-2 border-cyan-400/50">
              <CheckCircle2 className="h-9 w-9 text-cyan-300" />
            </div>
            <h2 className="mt-5 font-head text-2xl font-extrabold text-white">Thank you!</h2>
            <p className="mt-2 text-sm text-slate-300">Our team will reach out to you shortly.</p>
            <KioskButton testId="kiosk-connect-done" className="mt-6 w-full" onClick={onClose}>Done</KioskButton>
          </div>
        ) : (
          <form onSubmit={submit} data-testid="kiosk-connect-form">
            <h2 className="font-head text-2xl sm:text-3xl font-extrabold text-white">Connect With Us</h2>
            <p className="mt-1.5 text-sm text-slate-400">Share your details and our team will get in touch.</p>

            <div className="mt-6 space-y-3">
              <Field icon={UserRound} testId="connect-name" type="text" placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} />
              <Field icon={Phone} testId="connect-mobile" type="tel" inputMode="tel" placeholder="Mobile Number" value={mobile} onChange={(e) => setMobile(e.target.value)} />
              <Field icon={Mail} testId="connect-email" type="email" inputMode="email" placeholder="Email ID" value={email} onChange={(e) => setEmail(e.target.value)} />
            </div>

            {err && <p className="mt-3 text-sm text-rose-400" data-testid="connect-error">{err}</p>}

            <KioskButton testId="connect-submit" type="submit" className="mt-6 w-full" disabled={loading}>
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Send className="h-5 w-5" />}
              {loading ? "Submitting..." : "Submit"}
            </KioskButton>
          </form>
        )}
      </motion.div>
    </motion.div>
  );
}
